import * as React from "react";
import { Dispatch, useState } from "react";
import styled from "@mui/material/styles/styled";
import { PanelsUtil } from "../util/PanelsUtil";
import { LevelSelectComponent } from "./LevelSelectComponent";
import { QualifiedFeature } from "fe-common/graphql";
import { DataInput } from "../../dataInputTypes";
import { CondensedTypography } from "fe-web-components/styles/typography";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { Suggestion } from "../hooks/useSuggestions";
import { DesktopOnly } from "../util/desktopOnly";
import { ClinicalReasoningIconDesktop } from "./clinicalReasoningIconDesktop";
import { MobileOnly } from "../util/mobileOnly";
import { ClinicalReasoningIconMobile } from "./clinicalReasoningIconMobile";
import { MobileClinicalReasoning } from "./procedureItem";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useScreenSize } from "../util/useScreenSize";
import { getFeatureFlags } from "fe-common/featureFlags/getFeatureFlags";
import { IconButton } from "@mui/material";
import { LevelNumericInput } from "./levelNumericInput";

//region [[ Styles ]]

const LevelPanelItemView = styled("div")(({ theme }) => ({
  borderTop: "1px solid #e2e1e2",
  "&:last-child": {
    borderBottom: "1px solid #e2e1e2",
  },
  display: "flex",
  paddingTop: "6px",
  paddingBottom: "6px",
  alignItems: "center",
  [theme.breakpoints.down(MobileView)]: {
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",

    paddingTop: "8px",
    paddingBottom: "8px",
    "&:last-child": {
      borderBottom: "unset",
    },
  },
}));

const DataInputSlideLabel = styled(CondensedTypography)(({ theme }) => ({
  fontSize: "16px",

  display: "flex",
  marginLeft: "0px",
  [theme.breakpoints.down(MobileView)]: {
    marginLeft: "5px",
    fontWeight: 400,
  },
  flexDirection: "row",
  alignItems: "center",
  alignContent: "center",
  overflow: "hidden",
}));

const LevelClinicalReasoning = styled("div")(({ theme }) => ({
  marginLeft: "15px",
}));

const NumericButton = styled(IconButton)(({ theme }) => ({
  fontSize: "18px",
  marginLeft: "7px",
}));

const LabelContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  width: "100%",
  [theme.breakpoints.down(MobileView)]: {
    display: "flex",
    flexDirection: "row",
  },
}));

const ListItemLevel = styled("li")(({ theme }) => ({
  display: "flex",

  alignItems: "center",
  alignContent: "center",
  flexDirection: "row-reverse",

  width: "100%",
  fontSize: "16px",
  marginRight: "8px",
  paddingLeft: "5px",
  paddingRight: "5px",
  [theme.breakpoints.down(MobileView)]: {
    display: "flex",
    flexDirection: "row-reverse",

    width: "100%",
    justifyContent: "space-around",
    flex: 1,
    marginRight: "0px",
    paddingLeft: "0px",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface LevelPanelItemProps {
  className?: string;
  qf: QualifiedFeature;
  dataInput?: DataInput;
  onChange: Dispatch<DataInput>;
  suggestion: Suggestion;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LevelPanelItem = ({ ...props }: LevelPanelItemProps) => {
  const [showClinicalReasoning, setShowClinicalReasoning] = useState(false);

  let primary = PanelsUtil.featureToPanelText(props.qf);
  const { isMobile } = useScreenSize();
  const usingNumericLabs = getFeatureFlags("numericLabs");

  const hasNumericValue = () => {
    return props.dataInput?.qualifiers &&
      props.dataInput?.qualifiers.length &&
      props.dataInput?.qualifiers[0].range?.low
      ? true
      : false;
  };

  const [numericEdit, setNumericEdit] = useState(hasNumericValue());

  const toggleNumericEdit = () => {
    setNumericEdit(!numericEdit);
  };

  const onShowClinicalReasoning = () => {
    if (!isMobile) return;
    evt({
      category: CATEGORY.PLAN,
      action: showClinicalReasoning
        ? ACTION.HIDE_CLINICAL_REASONING
        : ACTION.SHOW_CLINICAL_REASONING,
      label: props.qf.name,
    });

    setShowClinicalReasoning(!showClinicalReasoning);
  };

  return (
    <LevelPanelItemView className={props.className}>
      <ListItemLevel>
        {usingNumericLabs && (
          <NumericButton onClick={toggleNumericEdit}>
            <i className="fas fa-exchange"></i>
          </NumericButton>
        )}

        {(!numericEdit || !usingNumericLabs) && (
          <LevelSelectComponent
            qf={props.qf}
            onChange={props.onChange}
            response={props.dataInput}
          />
        )}

        {numericEdit && usingNumericLabs && (
          <LevelNumericInput
            onChange={props.onChange}
            response={props.dataInput}
          />
        )}

        <LabelContainer>
          <DataInputSlideLabel>{primary}</DataInputSlideLabel>
          {props.suggestion && (
            <>
              <DesktopOnly>
                <ClinicalReasoningIconDesktop suggestion={props.suggestion} />
              </DesktopOnly>
              <MobileOnly>
                <ClinicalReasoningIconMobile
                  showClinicalReasoning={showClinicalReasoning}
                  onShowClinicalReasoning={onShowClinicalReasoning}
                />
              </MobileOnly>
            </>
          )}
        </LabelContainer>
      </ListItemLevel>

      {showClinicalReasoning && (
        <LevelClinicalReasoning>
          <MobileClinicalReasoning
            show={showClinicalReasoning}
            suggestion={props.suggestion}
          />
        </LevelClinicalReasoning>
      )}
    </LevelPanelItemView>
  );
};
